import { useCallback, useEffect, useState } from 'react';
import { documentsAPI, Document } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ListPager, ListSearch, useListControls } from '../../components/common/listControls';
import { AlertCircle, Archive, ArchiveRestore, Download, Loader2 } from 'lucide-react';
import { useDocumentArchive } from './useDocumentArchive';

interface ArchivedDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Refresh the main documents view after something is restored. */
  onRestored: () => void | Promise<void>;
  onDownload: (doc: Document) => void;
}

const PAGE_FETCH = 500;

/**
 * Lists archived documents with search and paging, and restores them.
 * Archived documents stay downloadable; restoring puts them back in their folder.
 */
export default function ArchivedDocumentsDialog({ open, onClose, onRestored, onDownload }: ArchivedDocumentsDialogProps) {
  const { formatDate } = useFormatters();
  const [docs, setDocs] = useState<Document[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await documentsAPI.getDocuments({ archived: 'true', page_size: PAGE_FETCH });
      setDocs(res.data.results);
      setTotal(res.data.count);
    } catch (err: unknown) {
      console.error('Error loading archived documents:', err);
      setError(getApiErrorMessage(err, 'Failed to load archived documents'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const { restoreDocument, busy } = useDocumentArchive({
    onChanged: async () => {
      await load();
      await onRestored();
    },
  });

  const list = useListControls(docs, {
    searchText: (d) => [d.title, d.category_name, d.company_name, d.uploaded_by_name].filter(Boolean).join(' '),
    sorters: { uploaded: (d) => d.version_uploaded_at || d.uploaded_at },
    defaultSort: { key: 'uploaded', dir: 'desc' },
    tieBreak: (d) => d.title,
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="archived-docs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Archived documents
          </DialogTitle>
          <DialogDescription>
            Archived documents are hidden from the folders but kept. Restore one to put it back in its folder.
          </DialogDescription>
        </DialogHeader>

        {loading && docs.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="archived-docs-loading">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            Loading archived documents…
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center" data-testid="archived-docs-error">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <p className="text-sm text-red-600">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void load()}>Try again</Button>
          </div>
        ) : docs.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground" data-testid="archived-docs-empty">
            <Archive className="h-8 w-8" />
            <p className="text-sm">No archived documents.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <ListSearch
              value={list.search}
              onChange={list.setSearch}
              placeholder="Search archived documents…"
              testId="archived-docs-search"
            />
            {total > docs.length && (
              <p className="text-xs text-muted-foreground">
                Showing the {docs.length} most recent of {total} archived documents.
              </p>
            )}
            <ListPager list={list} position="top" noun="documents" testIdPrefix="archived-docs" />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Folder</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.pageRows.map((doc) => (
                  <TableRow key={doc.id} data-testid={`archived-doc-${doc.id}`}>
                    <TableCell className="font-medium max-w-xs truncate" title={doc.title}>{doc.title}</TableCell>
                    <TableCell className="text-muted-foreground">{doc.category_name || 'Uncategorized'}</TableCell>
                    <TableCell className="text-muted-foreground">{doc.company_name || '—'}</TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatDate(doc.version_uploaded_at || doc.uploaded_at)}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => onDownload(doc)}>
                        <Download className="h-4 w-4 mr-1.5" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => void restoreDocument(doc)}
                        data-testid={`archived-doc-restore-${doc.id}`}
                      >
                        <ArchiveRestore className="h-4 w-4 mr-1.5" />
                        Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {list.pageRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">
                      No archived documents match your search.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ListPager list={list} position="bottom" noun="documents" testIdPrefix="archived-docs" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
